import React, { type ReactNode } from 'react';
import { css, useTheme } from '@emotion/react';
import styled from 'types/theme.types';
import { ReactComponent as env0Logo } from 'assets/images/logo-white.svg';
import { ReactComponent as envZeroFullWhite } from 'assets/images/env-zero-full-white.svg';
import { Helmet } from 'react-helmet';
import logo3d from 'assets/images/logo-3d.png';
import { legacyOnly, newDesignOnly } from 'constants/themes.constants';

/**
 * The 3D logo backdrop goes with the old brand, so the new design drops it and keeps the panel flat -
 * the design file specifies no illustrated background for any shell. Rendering it inside the branch
 * rather than hiding it with CSS keeps the image request off the new-design path entirely.
 */
export const OnboardingLayout: React.FC<{ children?: ReactNode }> = ({ children }) => {
  const theme = useTheme();

  return (
    <>
      <Helmet>
        <title>env0 - Onboarding</title>
      </Helmet>
      <OuterContainer>
        <MainLayoutContainer>
          {!theme.isNewDesign && <BackgroundLogo src={logo3d} />}
          {theme.isNewDesign ? <EnvZeroFullLogo /> : <Env0Logo />}
          <WhiteContainer>{children}</WhiteContainer>
        </MainLayoutContainer>
      </OuterContainer>
    </>
  );
};

const BackgroundLogo = styled.img`
  position: absolute;
  right: 5%;
  bottom: 0;
  width: 50%;
  height: 100%;
  z-index: 0;
  rotate: 5deg;
  @media (max-width: 768px) {
    width: 100%;
    height: 50%;
  }
`;

const OuterContainer = styled.div`
  width: 100%;
  height: 100vh;
  display: flex;
`;

const MainLayoutContainer = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
  overflow: auto;

  ${legacyOnly(
    theme => css`
      background: ${theme.secondaryBlack};
    `
  )}

  ${newDesignOnly(css`
    background: var(--surface-navigation-navigation);
  `)}

  background-size: cover;
`;

const Env0Logo = styled(env0Logo)`
  margin: 50px;
  transform: scale(1.3);
  flex: none;
`;

const EnvZeroFullLogo = styled(envZeroFullWhite)`
  height: 40px;
  width: auto;
  margin: 50px;
  flex: none;
`;

const cardWidth = '970px' as const;
const WhiteContainer = styled.div`
  display: flex;
  overflow: auto;
  width: ${cardWidth};
  height: 80%;
  min-height: 750px;

  ${legacyOnly(
    theme => css`
      border-radius: 4px;
      background: ${theme.primaryWhite};
    `
  )}

  ${newDesignOnly(
    theme => css`
      border-radius: ${theme.radius['200']};
      background: var(--surface-main-cards);
    `
  )}

  position: relative;
  z-index: 1;

  @media (max-width: ${cardWidth}) {
    width: 100%;
    height: min-content;
    border-radius: 0;
    flex: 1;
  }
`;

export default OnboardingLayout;
